import React, { useState, useEffect } from 'react';
import './UsersList.css';
import { API_URL } from '../config/api';

const UsersList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userHistory, setUserHistory] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', phone_id: '' });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showLinkPhoneModal, setShowLinkPhoneModal] = useState(false);
  const [linkPhoneUserId, setLinkPhoneUserId] = useState(null);
  const [newPhoneId, setNewPhoneId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`);
      const data = await response.json();
      setUsers(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error);
      setLoading(false);
    }
  };

  const fetchUserHistory = async (userId) => {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}/history`);
      const data = await response.json();
      setUserHistory(data);
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserHistory(user.id);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    if (!newUser.name.trim()) {
      setError('Le nom est requis');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newUser.name.trim(),
          email: newUser.email.trim() || null,
          phone_id: newUser.phone_id.trim() || null
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Utilisateur créé avec succès !');
        setNewUser({ name: '', email: '', phone_id: '' });
        setShowAddForm(false);
        // Rafraîchir la liste
        fetchUsers();
        // Réinitialiser le message de succès après 3 secondes
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Erreur lors de la création de l\'utilisateur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${userName}" ?\n\nCette action supprimera également toutes ses demandes et scans. Cette action est irréversible.`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Utilisateur "${userName}" supprimé avec succès`);
        // Rafraîchir la liste
        fetchUsers();
        // Réinitialiser la sélection si l'utilisateur supprimé était sélectionné
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser(null);
          setUserHistory([]);
        }
        // Réinitialiser le message de succès après 3 secondes
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Erreur lors de la suppression de l\'utilisateur');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion au serveur');
    }
  };

  const handleUnlinkPhone = async (userId, userName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir délier le téléphone de "${userName}" ?\n\nL'utilisateur devra sélectionner son profil à nouveau lors de son prochain scan.`)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      const response = await fetch(`${API_URL}/api/users/${userId}/unlink-phone`, {
        method: 'POST',
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(`Téléphone délié avec succès pour "${userName}"`);
        fetchUsers();
        if (selectedUser && selectedUser.id === userId) {
          setSelectedUser({ ...selectedUser, phone_id: null });
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Erreur lors du déliage du téléphone');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion au serveur');
    }
  };

  const handleLinkPhone = async (userId) => {
    setLinkPhoneUserId(userId);
    setNewPhoneId('');
    setShowLinkPhoneModal(true);
  };

  const handleLinkPhoneSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    if (!newPhoneId.trim()) {
      setError('L\'ID du téléphone est requis');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/users/link-phone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone_id: newPhoneId.trim(),
          user_id: linkPhoneUserId
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Téléphone lié avec succès !');
        setShowLinkPhoneModal(false);
        setNewPhoneId('');
        setLinkPhoneUserId(null);
        fetchUsers();
        if (selectedUser && selectedUser.id === linkPhoneUserId) {
          setSelectedUser(data.user);
        }
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Erreur lors de la liaison du téléphone');
      }
    } catch (error) {
      console.error('Erreur:', error);
      setError('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="users-list">
      <div className="users-header">
        <h1>Liste des utilisateurs</h1>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? '❌ Annuler' : '➕ Ajouter un utilisateur'}
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <strong>Erreur :</strong> {error}
          <button onClick={() => setError(null)} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <strong>Succès :</strong> {success}
          <button onClick={() => setSuccess(null)} className="alert-close">×</button>
        </div>
      )}

      {showAddForm && (
        <div className="card add-user-form">
          <h2>Ajouter un nouvel utilisateur</h2>
          <form onSubmit={handleAddUser}>
            <div className="form-group">
              <label htmlFor="name">Nom *</label>
              <input
                type="text"
                id="name"
                className="form-control"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                placeholder="Nom et prénom"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email (optionnel)</label>
              <input
                type="email"
                id="email"
                className="form-control"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone_id">ID Téléphone (optionnel)</label>
              <input
                type="text"
                id="phone_id"
                className="form-control"
                value={newUser.phone_id}
                onChange={(e) => setNewUser({ ...newUser, phone_id: e.target.value })}
                placeholder="Laissez vide si l'utilisateur n'a pas encore scanné"
              />
              <small className="form-hint">
                L'ID téléphone sera généré automatiquement lors du premier scan si laissé vide
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                {isSubmitting ? 'Création...' : '✅ Créer l\'utilisateur'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setNewUser({ name: '', email: '', phone_id: '' });
                  setError(null);
                }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="users-container">
        <div className="card users-table-card">
          <h2>Utilisateurs ({users.length})</h2>
          {users.length === 0 ? (
            <p>Aucun utilisateur enregistré.</p>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>ID Téléphone</th>
                    <th>Scans totaux</th>
                    <th>Éléments utilisés</th>
                    <th>Date d'inscription</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr 
                      key={user.id}
                      className={selectedUser?.id === user.id ? 'selected' : ''}
                    >
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>{user.name}</td>
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                        {user.email ? (
                          <span style={{ color: '#d4af37' }}>{user.email}</span>
                        ) : (
                          <span style={{ color: '#666', fontStyle: 'italic' }}>-</span>
                        )}
                      </td>
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                        {user.phone_id ? (
                          <code className="phone-id-display">{user.phone_id.length > 20 ? user.phone_id.substring(0, 20) + '...' : user.phone_id}</code>
                        ) : (
                          <span style={{ 
                            color: '#999', 
                            fontStyle: 'italic',
                            padding: '0.375rem 0.75rem',
                            background: 'rgba(153, 153, 153, 0.1)',
                            borderRadius: '8px',
                            display: 'inline-block'
                          }}>Non lié</span>
                        )}
                      </td>
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                        <span style={{ 
                          color: (user.total_scans || 0) > 0 ? '#d4af37' : '#666',
                          fontWeight: (user.total_scans || 0) > 0 ? '600' : '400'
                        }}>{user.total_scans || 0}</span>
                      </td>
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>
                        <span style={{ 
                          color: (user.items_used || 0) > 0 ? '#d4af37' : '#666',
                          fontWeight: (user.items_used || 0) > 0 ? '600' : '400'
                        }}>{user.items_used || 0}</span>
                      </td>
                      <td onClick={() => handleUserClick(user)} style={{ cursor: 'pointer' }}>{formatDate(user.created_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {user.phone_id && (
                            <button
                              className="btn-unlink-phone"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnlinkPhone(user.id, user.name);
                              }}
                              title="Délier le téléphone de cet utilisateur"
                              style={{
                                padding: '0.375rem 0.75rem',
                                fontSize: '0.875rem',
                                backgroundColor: '#ff9800',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              🔓 Délier
                            </button>
                          )}
                          {!user.phone_id && (
                            <button
                              className="btn-link-phone"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLinkPhone(user.id);
                              }}
                              title="Lier un téléphone à cet utilisateur"
                              style={{
                                padding: '0.375rem 0.75rem',
                                fontSize: '0.875rem',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              📱 Lier
                            </button>
                          )}
                        <button
                          className="btn-delete-user"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteUser(user.id, user.name);
                          }}
                          title="Supprimer cet utilisateur"
                        >
                          🗑️ Supprimer
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedUser && (
          <div className="card user-history-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>Historique de {selectedUser.name}</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {selectedUser.phone_id ? (
                  <button
                    className="btn btn-warning"
                    onClick={() => handleUnlinkPhone(selectedUser.id, selectedUser.name)}
                    title="Délier le téléphone de cet utilisateur"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#ff9800',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🔓 Délier le téléphone
                  </button>
                ) : (
                  <button
                    className="btn btn-success"
                    onClick={() => handleLinkPhone(selectedUser.id)}
                    title="Lier un téléphone à cet utilisateur"
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#4CAF50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    📱 Lier un téléphone
                  </button>
                )}
              </div>
            </div>
            <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f5f5f5', borderRadius: '4px' }}>
              <p><strong>Email :</strong> {selectedUser.email || 'Non renseigné'}</p>
              <p><strong>ID Téléphone :</strong> {selectedUser.phone_id ? (
                <code style={{ background: '#e0e0e0', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>
                  {selectedUser.phone_id.length > 30 ? selectedUser.phone_id.substring(0, 30) + '...' : selectedUser.phone_id}
                </code>
              ) : (
                <span style={{ color: '#999', fontStyle: 'italic' }}>Non lié</span>
              )}</p>
            </div>
            {userHistory.length === 0 ? (
              <p>Aucun scan enregistré pour cet utilisateur.</p>
            ) : (
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Élément</th>
                      <th>Description</th>
                      <th>Taille</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userHistory.map((scan) => (
                      <tr key={scan.id}>
                        <td>{formatDate(scan.scan_date)}</td>
                        <td>{scan.item_name}</td>
                        <td>{scan.item_description || '-'}</td>
                        <td>{scan.item_size || '-'}</td>
                        <td>
                          {scan.is_historical ? (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background: 'rgba(33, 150, 243, 0.1)',
                              color: '#2196F3',
                              fontWeight: '500'
                            }}>
                              📜 Historique
                            </span>
                          ) : (
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              background: 'rgba(76, 175, 80, 0.1)',
                              color: '#4CAF50',
                              fontWeight: '500'
                            }}>
                              ✓ Système
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setSelectedUser(null);
                setUserHistory([]);
              }}
            >
              Fermer
            </button>
          </div>
        )}

        {showLinkPhoneModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowLinkPhoneModal(false);
              setNewPhoneId('');
              setLinkPhoneUserId(null);
              setError(null);
            }
          }}>
            <div className="modal-content">
              <h2>📱 Lier un téléphone</h2>
              <form onSubmit={handleLinkPhoneSubmit}>
                <div className="form-group">
                  <label htmlFor="newPhoneId">ID du téléphone *</label>
                  <input
                    type="text"
                    id="newPhoneId"
                    className="form-control"
                    value={newPhoneId}
                    onChange={(e) => setNewPhoneId(e.target.value)}
                    placeholder="Entrez l'ID du téléphone"
                    required
                    autoFocus
                  />
                  <small className="form-hint">
                    L'ID du téléphone est généré automatiquement lors du premier scan. 
                    Demandez à l'utilisateur de faire un scan pour obtenir cet ID, ou entrez-le manuellement.
                  </small>
                </div>
                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowLinkPhoneModal(false);
                      setNewPhoneId('');
                      setLinkPhoneUserId(null);
                      setError(null);
                    }}
                  >
                    Annuler
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? 'Liaison...' : '✅ Lier le téléphone'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersList;

